import apiClient from '@/lib/apiClient';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';
import type { ListParams } from '@/shared/types';

export type ImportedAd = {
  id: number;
  title: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'REJECTED';
  importSource: string | null;
  importSourceId: string | null;
  importSourceUrl: string | null;
  importedAt: string | null;
  cityName: string | null;
  cityId: number | null;
  ownerUserId: number | null;
  ownerName: string | null;
  ownerPhone: string | null;
};

export interface ImportedAdsListParams extends ListParams {
  source?: string;
  status?: string;
}

const ROOM_WANTED_BASE = '/admin/room-wanted-ads';

export const importedAdsApi = {
  list: async (params: ImportedAdsListParams) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get('/admin/imports/ads', { params: queryParams });
    return extractPaginatedData<ImportedAd>(res);
  },

  /** Admin approval: promote an imported DRAFT ad to PUBLISHED. */
  approve: async (id: number) => {
    const res = await apiClient.patch(`${ROOM_WANTED_BASE}/${id}`, { status: 'PUBLISHED' });
    return res.data?.data;
  },

  /** Reject an imported ad. */
  reject: async (id: number) => {
    const res = await apiClient.patch(`${ROOM_WANTED_BASE}/${id}`, { status: 'REJECTED' });
    return res.data?.data;
  },

  /** Approve (publish) many imported ads at once. Returns the affected count. */
  bulkApprove: async (ids: number[]): Promise<number> => {
    const res = await apiClient.post('/admin/room-wanted-ops/bulk-status', {
      adIds: ids,
      status: 'PUBLISHED',
    });
    return res.data?.data?.affected ?? ids.length;
  },

  /** Trigger a Roomaty import across all supported cities (runs in background). */
  runRoomaty: async () => {
    const res = await apiClient.post('/admin/imports/roomaty/run-all');
    return res.data?.data;
  },

  /** Trigger a Haraj ingest of the latest scraper output (no file upload). */
  runHaraj: async () => {
    const res = await apiClient.post('/admin/external-ads/import/haraj');
    return res.data?.data;
  },

  /** Trigger a Roomytk (roommatek.com) ingest of the latest scraper output. */
  runRoomytk: async () => {
    const res = await apiClient.post('/admin/external-ads/import/roomytk');
    return res.data?.data;
  },

  /** Collect the ids of all imported DRAFT ads matching the current source filter
   *  (across pages) so "approve all" can act on the whole pending set. */
  allDraftIds: async (params: ImportedAdsListParams): Promise<number[]> => {
    const queryParams = buildListParams({ ...params, status: 'DRAFT', page: 0, size: 1000 });
    const res = await apiClient.get('/admin/imports/ads', { params: queryParams });
    const data = extractPaginatedData<ImportedAd>(res);
    return (data.content || []).map((a) => a.id);
  },
};
